import React, { useState, useEffect } from 'react';
import './VexterLanding.css';
import logoLight from './logo/Vexter Media (Light font) without BG.png';
import cameraImage from './Images/Gemini_Generated_Image_ozu2aoozu2aoozu2.png';
import filmStripImage from './Images/photographic-film-vector-graphics-clip-art-reel-png-favpng-qPvZfTT1XnLcg1h9EVS9Arn1b-removebg-preview.png';
import emailjs from '@emailjs/browser';

const VexterLanding = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [showPhoneAnimation, setShowPhoneAnimation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleContactForm = () => {
    if (!isContactFormOpen) {
      // Check if mobile screen
      const isMobile = window.innerWidth <= 767;
      if (isMobile) {
        // Mobile: Open form immediately without animation
        setIsContactFormOpen(true);
      } else {
        // Desktop: Start phone animation
        setShowPhoneAnimation(true);
        setTimeout(() => {
          setIsContactFormOpen(true);
        }, 800); // Delay form opening to allow phone animation
      }
    } else {
      // Closing form - hide phone animation
      setIsContactFormOpen(false);
      setShowPhoneAnimation(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration
      const serviceId = 'service_cmsdvbd';
      const templateId = 'template_ved36gy';
      const publicKey = 'Zo5bVRObbaT9ifpRo';

      // Prepare email template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone_number: formData.phone,
        message: formData.message,
        name: formData.name,
        email: formData.email
      };

      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      // Success
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      // Close form after 2 seconds
      setTimeout(() => {
        setIsContactFormOpen(false);
        setShowPhoneAnimation(false);
        setSubmitStatus(null);
      }, 2000);
      
    } catch (error) {
      // Error
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vexter-landing">
      {/* SVG Gradients for Instagram */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f09433" />
            <stop offset="25%" stopColor="#e6683c" />
            <stop offset="50%" stopColor="#dc2743" />
            <stop offset="75%" stopColor="#cc2366" />
            <stop offset="100%" stopColor="#bc1888" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Starry Night Background */}
      <div className="starry-night">
        <div className="stars-container">
          {[...Array(100)].map((_, i) => (
            <div key={i} className={`star star-${i + 1}`}></div>
          ))}
        </div>
      </div>
      
      {/* Cursor Following Animation */}
      <div 
        className="cursor-gradient"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 20%, transparent 50%)`
        }}
      ></div>
      
      {/* Floating Particles */}
      <div className="particles-container">
        <div className="particle particle-1"></div>
        <div className="particle particle-2"></div>
        <div className="particle particle-3"></div>
        <div className="particle particle-4"></div>
        <div className="particle particle-5"></div>
        <div className="particle particle-6"></div>
      </div>

        {/* Asteroids with Cinema Cameras */}
        <div className="asteroids-container">
          <div className="asteroid asteroid-1">
            <div className="cinema-camera">🎥</div>
          </div>
          <div className="asteroid asteroid-2">
            <div className="cinema-camera">📹</div>
          </div>
          <div className="asteroid asteroid-3">
            <div className="cinema-camera">🎬</div>
          </div>
          <div className="asteroid asteroid-4">
            <div className="cinema-camera">📽️</div>
          </div>
          <div className="asteroid asteroid-5">
            <div className="cinema-camera">🎥</div>
          </div>
          <div className="asteroid asteroid-6">
            <div className="cinema-camera">📹</div>
          </div>
        </div>

        {/* Floating Film Director with Welcome Sign */}
        <div className="director-container">
          <div className="film-director">
            <div className="director-body">🎬</div>
            <div className="megaphone">📢</div>
            <div className="welcome-sign">
              <div className="wooden-plank">
                <span className="sign-text">Lights, Camera, Action! <br /> With Vexter Media</span>
              </div>
            </div>
          </div>
        </div>

      {/* Header */}
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <a href="#home" className="nav-brand">
            <img src={logoLight} alt="Vexter Media" className="logo" />
            <div className="brand-text">
              <span className="brand-vexter">Vexter</span>
              <span className="brand-media">Media</span>
            </div>
          </a>
          <nav className="nav-menu">
            <a href="#who-we-are" className="nav-link" onClick={closeMobileMenu}>Who We Are</a>
            <a href="#services" className="nav-link" onClick={closeMobileMenu}>Services</a>
            <a href="#about" className="nav-link" onClick={closeMobileMenu}>About Us</a>
            <a href="#contact" className="nav-link" onClick={closeMobileMenu}>Contact</a>
          </nav>
          
          {/* Mobile Menu Button */}
          <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
            <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          
          {/* Mobile Dropdown Menu */}
          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <a href="#who-we-are" className="mobile-nav-link" onClick={closeMobileMenu}>Who We Are</a>
            <a href="#services" className="mobile-nav-link" onClick={closeMobileMenu}>Services</a>
            <a href="#about" className="mobile-nav-link" onClick={closeMobileMenu}>About Us</a>
            <a href="#contact" className="mobile-nav-link" onClick={closeMobileMenu}>Contact</a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="title-white">Capturing Stories</span>
              <span className="title-gradient"> Creating Impact</span>
            </h1>
            <p className="hero-subtitle">
              Vexter Media is a full-service media production and distribution company dedicated to bringing your vision to life.
            </p>
            <a href="#contact" className="cta-button">Explore Our Services</a>
          </div>
        </div>
      </section>

          {/* Who We Are Section */}
          <section id="who-we-are" className="who-we-are">
            <div className="container">
              <div className="section-content">
                <div className="camera-container">
                  <div className="film-camera">
                    <img src={cameraImage} alt="Film Camera" className="camera-image" />
                    <div className="spotlight-beam"></div>
                  </div>
                </div>
                <div className="spotlight-content">
                  <h2 className="section-title">Who We Are</h2>
                  <div className="section-text">
                    <p>
                      Founded on a passion for content, Vexter Media combines technical expertise with creative vision to produce and distribute compelling content. Our team of experienced professionals works closely with clients to deliver exceptional results, from concept to final delivery.
                    </p>
                    <p>
                      We specialize in a wide range of media formats, including movies, TV content, short videos, documentaries, promotional content, and independent films, ensuring your message reaches its intended audience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <h2 className="section-title">Our Services</h2>
          <div className="services-list">
            <div className="service-item service-right">
              <div className="service-content">
                <h3 className="service-title">Distribution</h3>
                <p className="service-description service-short">
                  Seamlessly delivering your films and shows across theaters, TV, and digital platforms worldwide.
                </p>
                <p className="service-description service-full">
                  We are a global launchpad for your content. With a powerful network spanning the UAE, Singapore, Hong Kong, and beyond, we ensure your films and shows reach audiences effortlessly. We handle every detail of the distribution process, from theatrical releases to digital and television formats, ensuring your content finds its rightful home.
                </p>
              </div>
              <div className="service-icon">🎬</div>
            </div>
            
            <div className="service-item service-left">
              <div className="service-content">
                <h3 className="service-title">Partnerships</h3>
                <p className="service-description service-short">
                  Connecting creators with top studios, broadcasters, and OTT platforms to maximize impact and profitability.
                </p>
                <p className="service-description service-full">
                  We build bridges between brilliant ideas and the right resources. At Vexter Media, we connect talented creators with prestigious studios, broadcasters, and OTT platforms worldwide. Our focus is on forging lucrative deals and maximizing profitability, so your vision can achieve its full potential.
                </p>
              </div>
              <div className="service-icon">🤝</div>
            </div>
            
            <div className="service-item service-right">
              <div className="service-content">
                <h3 className="service-title">Content Acquisition</h3>
                <p className="service-description service-short">
                  Securing rights for bold, compelling stories and bringing them to global audiences.
                </p>
                <p className="service-description service-full">
                  We are constantly on the hunt for the next great story. Our team is passionate about acquiring media rights for compelling, edgy cinema in Hindi and other regional languages. We leverage our robust partnerships with leading companies and channels to secure rights across all platforms, ensuring these films get the audience they deserve.
                </p>
              </div>
              <div className="service-icon">📺</div>
            </div>
            
            <div className="service-item service-left">
              <div className="service-content">
                <h3 className="service-title">Production & Co-Production</h3>
                <p className="service-description service-short">
                  Guiding your project from script to screen with full-scale production expertise.
                </p>
                <p className="service-description service-full">
                  From a powerful script to the final frame, we are your full-service production partner. Our expertise spans every genre, and we handle every facet of the production process from script finalization and casting to financing. We are here to guide your project from concept to a complete, high-quality film with no constraints.
                </p>
              </div>
              <div className="service-icon">🎥</div>
            </div>
            
            <div className="service-item service-right">
              <div className="service-content">
                <h3 className="service-title">Dubbing & Subtitling</h3>
                <p className="service-description service-short">
                  Breaking language barriers with high-quality dubbing and subtitles in major Indian languages.
                </p>
                <p className="service-description service-full">
                  We believe that great stories should be heard by everyone. Our dubbing and subtitling services break down language barriers, making your content accessible and region-friendly. We deliver exceptional quality in all major Indian regional languages, including English, Hindi, Tamil, Telugu, Bengali, Marathi, Kannada, Malayalam, Punjabi, and many more.
                </p>
              </div>
              <div className="service-icon">🗣️</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="about">
        <div className="film-strip-background">
          <img src={filmStripImage} alt="Film Strip" className="film-strip-image" />
        </div>
        <div className="container">
          <h2 className="section-title">About Us</h2>
          <div className="about-content">
            <p className="about-text">
              Welcome to Vexter Media a modern film and digital media content house built for the future of entertainment. We are a team of savvy movie aficionados who believe that authentic storytelling knows no bounds. Our mission is to look beyond the cliché and discover narratives that resonate with today's audiences, using the latest media techniques to bring them to life.
            </p>
            <p className="about-text">
              At our core, we are a trusted partner, an industry pivot, and a collaborative force. We take pride in our ability to deliver superlative media services and are driven by a passion for creating impactful content that makes a difference on every screen, everywhere.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title">Contact Us</h2>
          <div className="contact-content">
            <div className="contact-info">
              <p className="contact-question">Shall we chat?</p>
              <p className="contact-email">hi@vextermedia.in</p>
              <div className="talk-section">
                <span className="talk-text">Let's talk</span>
                <button className="talk-arrow-btn" onClick={toggleContactForm}>
                  →
                </button>
              </div>
            </div>
            
            {/* Phone Animation */}
            {showPhoneAnimation && (
              <div className="phone-animation">
                <div className="phone-icon">📞</div>
              </div>
            )}

            {/* Contact Form */}
            <div className={`contact-form-section ${isContactFormOpen ? 'open' : ''}`}>
              <div className="form-header">
                <h3>Get in touch</h3>
                <button className="close-form-btn" onClick={toggleContactForm}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <div className="input-with-icon">
                    <span className="input-icon">👤</span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <div className="input-with-icon">
                    <span className="input-icon">📞</span>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <div className="input-with-icon">
                    <span className="input-icon">✉️</span>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    required
                  ></textarea>
                </div>
                  <button type="submit" className="send-btn" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send'}
                  </button>
                  
                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="form-status success">
                      <span className="status-icon">✅</span>
                      <span>Message sent successfully! We'll get back to you soon.</span>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="form-status error">
                      <span className="status-icon">❌</span>
                      <span>Failed to send message. Please try again or contact us directly.</span>
                    </div>
                  )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
              <div className="social-media">
                <a href="https://www.facebook.com/profile.php?id=61581383411477" target="_blank" rel="noopener noreferrer" className="social-icon facebook">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/vexter_media" target="_blank" rel="noopener noreferrer" className="social-icon instagram">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/in/vexter-media-0409a7387/" target="_blank" rel="noopener noreferrer" className="social-icon linkedin">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            <div className="copyright">
              <p>&copy; Vexter Media 2025. All rights reserved.</p>
              <p>Creator: Shivanshu Kumar</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VexterLanding;
